import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Minus, Plus, Ticket, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { formatDate } from "@/components/EventCard";

export default function EventDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const refCode = params.get("ref");
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [type, setType] = useState(null);
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/events/${slug}`).then((r) => {
      setEvent(r.data);
      setType(r.data.ticket_types?.[0]?.name || null);
    }).catch(() => toast.error("Event not found"));
  }, [slug]);

  if (!event) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#e0481f]" /></div>;

  const selected = event.ticket_types?.find((t) => t.name === type);
  const total = selected ? (selected.price * qty).toFixed(2) : "0.00";

  const buy = async () => {
    if (!name || !email || !phone) { toast.error("Enter your name, email and phone"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        event_id: event.id, ticket_type: type, quantity: qty,
        customer_name: name, customer_email: email, customer_phone: phone, ref_code: refCode || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="relative h-[46vh] min-h-[300px] overflow-hidden grain">
        <img src={event.banner_image || event.flyer_image} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121110] via-[#121110]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 h-full flex flex-col justify-end pb-8">
          <span className="bg-[#e0481f] text-white text-xs font-display font-700 uppercase tracking-wider px-3 py-1 rounded-full w-fit mb-3">{event.category}</span>
          <h1 className="headline text-5xl sm:text-7xl text-[#f7f3ec]">{event.title}</h1>
          <p className="text-[#d4af37] text-lg mt-1">{event.subtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex flex-wrap gap-6 mb-8 text-[#d6cec2]">
            <span className="flex items-center gap-2"><Calendar size={18} className="text-[#e0481f]" /> {formatDate(event.date)}</span>
            <span className="flex items-center gap-2"><MapPin size={18} className="text-[#e0481f]" /> {event.location}</span>
          </div>
          <h2 className="headline text-3xl text-[#f7f3ec] mb-3">About This Ride</h2>
          <p className="text-[#d6cec2] text-lg leading-relaxed whitespace-pre-line">{event.description}</p>
          {event.flyer_image && event.flyer_image !== event.banner_image && (
            <img src={event.flyer_image} alt="flyer" className="mt-8 rounded-2xl w-full max-w-md gold-border" />
          )}
        </div>

        {/* Checkout */}
        <div className="lg:col-span-1">
          <div className="card-warm rounded-2xl p-6 sticky top-24" data-testid="checkout-card">
            <h3 className="headline text-2xl text-[#f7f3ec] mb-1">Get Tickets</h3>
            {refCode && <p className="text-xs text-[#d4af37] mb-4 flex items-center gap-1.5"><ShieldCheck size={14} /> Credited to Ryder {refCode}</p>}

            <label className="eyebrow text-xs text-[#8f857a] block mb-2 mt-3">Ticket Type</label>
            <div className="space-y-2 mb-4">
              {event.ticket_types?.map((t) => (
                <button key={t.name} onClick={() => setType(t.name)} className={`w-full flex justify-between items-start rounded-xl px-4 py-3 border transition-colors ${type === t.name ? "border-[#e0481f] bg-[#e0481f]/10" : "border-[#3a332c]"}`} data-testid={`ticket-type-${t.name}`}>
                  <span className="text-left">
                    <span className="block text-[#f7f3ec] text-sm font-medium">{t.name}</span>
                    {t.description && <span className="block text-[#a9a196] text-xs mt-0.5">{t.description}</span>}
                  </span>
                  <span className="headline text-lg gold-text shrink-0">${t.price}</span>
                </button>
              ))}
            </div>

            <label className="eyebrow text-xs text-[#8f857a] block mb-2">Quantity</label>
            <div className="flex items-center gap-4 mb-4">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 rounded-full gold-border flex items-center justify-center text-[#f7f3ec]" data-testid="qty-minus"><Minus size={16} /></button>
              <span className="headline text-2xl text-[#f7f3ec] w-8 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-10 rounded-full gold-border flex items-center justify-center text-[#f7f3ec]" data-testid="qty-plus"><Plus size={16} /></button>
            </div>

            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 mb-3 text-[#f7f3ec] placeholder:text-[#6b645b] focus:border-[#e0481f] outline-none" data-testid="checkout-name" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" type="email" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 mb-4 text-[#f7f3ec] placeholder:text-[#6b645b] focus:border-[#e0481f] outline-none" data-testid="checkout-email" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" type="tel" autoComplete="tel" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 mb-4 text-[#f7f3ec] placeholder:text-[#6b645b] focus:border-[#e0481f] outline-none" data-testid="checkout-phone" />

            <div className="flex justify-between items-center mb-4 pt-3 border-t border-[#3a332c]">
              <span className="text-[#a9a196]">Total</span>
              <span className="headline text-3xl gold-text" data-testid="checkout-total">${total}</span>
            </div>
            <button onClick={buy} disabled={loading} className="w-full btn-ember rounded-full py-3.5 font-display uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkout-buy">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Ticket size={18} />} {loading ? "Processing" : "Buy Now"}
            </button>
            <p className="text-[10px] text-[#6b645b] text-center mt-3">Secure checkout powered by Stripe</p>
          </div>
        </div>
      </div>
    </div>
  );
}
